import React, { ReactNode, useRef, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';

import { ContentContainer, ScrollableContent } from '../components/styled';
import { ContactsButton } from '../components/styled/ContactsButton';
import { SubmitButtonGroup } from '../components/styled/ContactsForm';
import { useUserContext } from '../contexts/UserContext';
import { ICsvUploadResponse, upload } from '../api/ImportUploadApi';
import ContactsSpinner from '../components/ContactsSpinner';
import { useNavigate } from 'react-router';

const ImportCsv = () => {
  const navigate = useNavigate();
  const userContext = useUserContext();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileSelected, setFileSelected] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<ICsvUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFileChange = () => {
    setFileSelected(true);
  };

  const onSubmit = async () => {
    if (fileRef.current !== null && fileRef.current.files?.[0] !== undefined) {
      try {
        setSubmitting(true);
        setError(null);
        const response = await upload(userContext.getAuthHeader(), fileRef.current.files?.[0]);
        if (response.isValid) {
          setUploadResponse(response);
        } else {
          setError('Sorry, that CSV is not in a format we recognize.');
        }
      } catch (e) {
        setError(
          'Sorry, something went wrong. Please try again, and if the problem persists, contact Customer Support.');
      }
      setSubmitting(false);
    }
  };

  const onBack = () => {
    navigate('/contacts', { replace: true });
  };

  let content: ReactNode;

  if (submitting) {
    content = <ContactsSpinner/>;
  } else if (uploadResponse === null) {
    content = <>
      <Form.Control type="file" accept=".csv" onChange={onFileChange} ref={fileRef}/>
      <SubmitButtonGroup>
        <ContactsButton hover={true} type="submit" disabled={!fileSelected} onClick={onSubmit}>
          Import
        </ContactsButton>
      </SubmitButtonGroup>
    </>;
  } else if (uploadResponse.isValid) {
    content = <>
      <Alert variant={'success'}>
        Successfully imported {uploadResponse.rowsImported} {uploadResponse.rowsImported === 1 ? 'row' : 'rows'},
        and dropped {uploadResponse.rowsDropped} {uploadResponse.rowsDropped ? 'row' : 'rows'}.
      </Alert>

      <SubmitButtonGroup>
        <ContactsButton hover={true} type="button" onClick={onBack}>
          Back to Contacts
        </ContactsButton>
      </SubmitButtonGroup>
    </>;
  }

  return (
    <ContentContainer>
      <ScrollableContent>
        <h3>
          Import Contacts
        </h3>
        <p>
          You can import your contacts in a standard CSV (comma separated values) file.
        </p>
        <p>
          We currently support CSV files exported from <a
          href="https://www.postable.com/" target="_blank" rel="noreferrer">Postable</a>.
        </p>
        {
          error ? <Alert variant="danger">
            {error}
          </Alert> : null
        }
        {content}
      </ScrollableContent>
    </ContentContainer>
  );
};

export default ImportCsv;
